import React, { useEffect } from 'react';
import { Box, Drawer, Typography, IconButton, useTheme, Divider, Tooltip, Paper } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import AnalysisOutputGroup from 'sections/lp-analysis/AnalysisOutputGroup';
import PictureViewer from './PictureViewer';
import MarketAnalysisReportViewer from './MarketAnalysisReportViewer';
import GetWhoToBuyReportViewer from './GetWhoToBuyReportViewer';
import NewCompetitorsAnalysisResult from 'sections/premiumBlock/NewCompetitorsAnalysisResult';
import { addCompetitorsAnalysisResults } from 'store/reducers/actions';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import config from 'config';
import { CircularProgress } from '@mui/material';
import { useDispatch } from 'react-redux';
import { setAnalysisUrl, setCurrentTaskId, setHistoryResult, setExtendedAnalysisResults } from 'store/reducers/actions';

const ReportViewer = ({ open, onClose, reportData, reportType, inline = false }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [cmpLoading, setCmpLoading] = React.useState(false);
  const [cmpLoaded, setCmpLoaded] = React.useState(false);
  const [lastLoadedTaskId, setLastLoadedTaskId] = React.useState(null);
  const contentRef = React.useRef(null);
  const [selectionText, setSelectionText] = React.useState('');
  const [toolbarPos, setToolbarPos] = React.useState({ top: 0, left: 0, visible: false });
  const [copiedTick, setCopiedTick] = React.useState(false);
  const [askedTick, setAskedTick] = React.useState(false);

  // Load the report data into Redux store when the report viewer opens
  useEffect(() => {
    if (open && reportData) {
      console.log('ReportViewer: Dispatching data to Redux');
      console.log('reportData:', reportData);
      console.log('reportData.analysisData:', reportData.analysisData);
      console.log('reportData keys:', Object.keys(reportData));

      // Check if analysisData exists, if not, look for other possible properties
      if (!reportData.analysisData) {
        console.log('ReportViewer: No analysisData found, checking other properties...');
        console.log('reportData.data:', reportData.data);
        console.log('reportData.result:', reportData.result);
        console.log('reportData.content:', reportData.content);
        console.log('reportData.metadata:', reportData.metadata);
      }

      // Check if we have analysisData, if not, try to find the data in other properties
      let analysisData = reportData.analysisData;

      if (!analysisData) {
        console.log('ReportViewer: No analysisData found, checking other properties...');
        // Try different possible property names
        analysisData = reportData.data || reportData.result || reportData.content || reportData.metadata;
        console.log('ReportViewer: Found analysisData in:', analysisData ? 'alternative property' : 'none');
      }

      if (!analysisData) {
        console.log('ReportViewer: No analysis data found in any property');
        return;
      }

      // Normalize overview payload shape for LP analysis
      const normalizedOverview = (function () {
        try {
          return (
            analysisData?.overview_result?.result?.[0] ||
            analysisData?.overview?.result?.[0] ||
            analysisData?.answer_data?.result?.[0] ||
            analysisData?.result?.[0] ||
            analysisData?.overview_result?.[0] ||
            analysisData?.overview?.[0] ||
            (typeof analysisData === 'object' ? analysisData : null)
          );
        } catch (e) {
          return typeof analysisData === 'object' ? analysisData : null;
        }
      })();

      // Extract task ID and URL from analysisData (best-effort)
      const taskId =
        analysisData.task_id || analysisData?.overview_result?.task_id || analysisData?.metadata?.task_id || reportData.task_id;
      const url = analysisData.url || analysisData?.overview_result?.url || analysisData?.metadata?.url || reportData.url;

      console.log('ReportViewer: taskId:', taskId, 'url:', url);
      console.log('ReportViewer: competitors_search_task:', analysisData.competitors_search_task);

      if (taskId) {
        dispatch(setCurrentTaskId(taskId));
      }
      if (url) {
        dispatch(setAnalysisUrl(url));
      }

      if (normalizedOverview) {
        // Dispatch the analysis data to Redux in the expected shape
        dispatch(
          setHistoryResult({
            url: url || '',
            task_id: taskId || '',
            answer_data: {
              result: [normalizedOverview]
            },
            usp: analysisData.usp,
            market_overview: analysisData.market_overview,
            market_trends_and_benchmarks: analysisData.market_trends,
            segmentation: analysisData.segmentation,
            swot: analysisData.swot,
            icp: analysisData.icp,
            pricing_analysis: analysisData.pricing_analysis,
            competitors_search_task: analysisData.competitors_search_task?.result || analysisData.competitors_search_task
          })
        );

        // Also dispatch the extended analysis results if present
        dispatch(
          setExtendedAnalysisResults({
            usp: analysisData.usp,
            market_overview: analysisData.market_overview,
            market_trends_and_benchmarks: analysisData.market_trends,
            segmentation: analysisData.segmentation,
            swot: analysisData.swot,
            icp: analysisData.icp,
            pricing_analysis: analysisData.pricing_analysis,
            competitors_search: analysisData.competitors_search
          })
        );
        console.log('ReportViewer: Data dispatched to Redux successfully');
      } else {
        console.log('ReportViewer: No normalized overview could be derived');
      }
    }
  }, [open, reportData, dispatch]);

  // Selection handling to show inline mini-toolbar (Ask in Chat / Copy)
  React.useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const updateToolbarFromSelection = () => {
      try {
        const sel = window.getSelection();
        if (!sel || sel.isCollapsed || !sel.rangeCount) {
          setToolbarPos((p) => (p.visible ? { ...p, visible: false } : p));
          setSelectionText('');
          return;
        }
        const range = sel.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        if (!rect || (rect.width === 0 && rect.height === 0)) {
          setToolbarPos((p) => (p.visible ? { ...p, visible: false } : p));
          setSelectionText('');
          return;
        }
        const containerRect = el.getBoundingClientRect();
        // Ensure selection is within the content container vertically
        if (rect.bottom < containerRect.top || rect.top > containerRect.bottom) {
          setToolbarPos((p) => (p.visible ? { ...p, visible: false } : p));
          setSelectionText('');
          return;
        }
        const text = String(sel.toString() || '').trim();
        if (!text) {
          setToolbarPos((p) => (p.visible ? { ...p, visible: false } : p));
          setSelectionText('');
          return;
        }
        const scrollTop = el.scrollTop || 0;
        const scrollLeft = el.scrollLeft || 0;
        const top = scrollTop + (rect.top - containerRect.top) - 36; // show above selection
        const left = scrollLeft + (rect.right - containerRect.left); // anchor to right edge
        setSelectionText(text);
        setToolbarPos({ top: Math.max(0, top), left: Math.max(0, left), visible: true });
      } catch (_) {
        // ignore
      }
    };

    const hideToolbar = () => {
      setToolbarPos((p) => (p.visible ? { ...p, visible: false } : p));
    };

    el.addEventListener('mouseup', updateToolbarFromSelection);
    el.addEventListener('keyup', updateToolbarFromSelection);
    el.addEventListener('scroll', hideToolbar, { passive: true });

    return () => {
      el.removeEventListener('mouseup', updateToolbarFromSelection);
      el.removeEventListener('keyup', updateToolbarFromSelection);
      el.removeEventListener('scroll', hideToolbar);
    };
  }, [contentRef]);

  const handleCopySelection = async () => {
    try {
      if (!selectionText) return;
      await navigator.clipboard.writeText(selectionText);
      setCopiedTick(true);
      setTimeout(() => setCopiedTick(false), 1000);
    } catch (_) {
      // ignore
    }
  };

  const handleAskInChat = () => {
    if (!selectionText) return;
    try {
      window.dispatchEvent(new CustomEvent('chat:insert_text', { detail: { text: selectionText } }));
      setAskedTick(true);
      setTimeout(() => setAskedTick(false), 1000);
    } catch (_) {
      // ignore
    }
  };

  const renderReportContent = () => {
    if (!reportData) return null;

    console.log('ReportViewer renderReportContent - reportType:', reportType);
    console.log('ReportViewer renderReportContent - reportData:', reportData);

    // Market Analysis Tool - dedicated renderer
    const isMarketAnalysis =
      reportType === 'functions.market_analysis_tool' ||
      reportType === 'market_analysis_tool' ||
      (reportData.analysisData && (reportData.analysisData.market_overview || reportData.analysisData.marketOverview));
    if (isMarketAnalysis) {
      return <MarketAnalysisReportViewer reportData={reportData} />;
    }

    // Get Who To Buy Strategy Tool - dedicated renderer
    const isGetWhoToBuy =
      reportType === 'functions.get_who_to_buy_strategy' ||
      reportType === 'get_who_to_buy_strategy' ||
      (reportData.analysisData && (reportData.analysisData.get || reportData.analysisData.client));
    if (isGetWhoToBuy) {
      return <GetWhoToBuyReportViewer reportData={reportData} />;
    }

    // For LP analysis, use the existing AnalysisOutputGroup component
    if (
      reportType === 'functions.landing_page_analysis' ||
      reportType === 'functions.lp_analysis' ||
      reportData.content_type === 'lpcompare.analysistask'
    ) {
      console.log('ReportViewer: Rendering AnalysisOutputGroup');
      return <AnalysisOutputGroup />;
    }

    // Picture viewer (generic): when reportType === 'picture' or generate_marketing_image
    const isPicture = reportType === 'picture' || reportType === 'generate_marketing_image';
    if (isPicture) return <PictureViewer data={reportData} />;

    // (LP handled above)

    // Competitors compare (v2) support
    const cmpData = reportData?.analysisData || reportData?.data || reportData?.result || reportData?.content || reportData?.metadata;
    const isCompetitors =
      (typeof reportType === 'string' && reportType.includes('compare_competitors')) ||
      (cmpData && (cmpData.url_list || cmpData.side_by_side_analysis || cmpData.field_by_field_analysis));
    const taskId = cmpData?.task_id || cmpData?.competitors_search_task?.task_id;
    if (isCompetitors) {
      if ((!cmpLoaded || lastLoadedTaskId !== taskId) && !cmpLoading && taskId) {
        setCmpLoading(true);
        const auth = getAuth();
        const apiUrl = config.apiUrl;
        auth.onAuthStateChanged((user) => {
          if (!user) {
            setCmpLoading(false);
            return;
          }
          user.getIdToken().then((token) => {
            axios
              .get(`${apiUrl}/lpcompare/get_old_comp_analysis/${taskId}`, {
                params: { use_v2: true },
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
              })
              .then((response) => {
                const responseData = response.data;
                const isNew = responseData.is_new_analysis === true;
                if (isNew) {
                  const dataWithFlag = { ...responseData, is_new_analysis: true };
                  dispatch(addCompetitorsAnalysisResults(dataWithFlag));
                  setCmpLoaded(true);
                  setLastLoadedTaskId(taskId);
                }
              })
              .finally(() => setCmpLoading(false));
          });
        });
      }

      if (cmpLoading && !cmpLoaded) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={24} />
          </Box>
        );
      }
      if (cmpLoaded) {
        return (
          <Box
            sx={{
              '& .MuiContainer-root': {
                maxWidth: '100% !important'
              }
            }}
          >
            <NewCompetitorsAnalysisResult showSecondaryMenu={false} />
          </Box>
        );
      }
      return (
        <Typography variant="body2" color="text.secondary">
          Comparison not available in v2 format.
        </Typography>
      );
    }

    // For other report types, show basic info
    console.log('ReportViewer: Rendering basic info');
    return (
      <Box>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          {reportData.title || 'Analysis Report'}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Status: {reportData.status || 'Unknown'}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Type: {reportData.content_type || 'Unknown'}
        </Typography>
        {reportData.url && (
          <Typography variant="body2" sx={{ mb: 1 }}>
            URL: {reportData.url}
          </Typography>
        )}
      </Box>
    );
  };

  if (!open) return null;

  if (inline) {
    return (
      <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Report Viewer
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Box ref={contentRef} sx={{ flex: 1, overflow: 'auto', position: 'relative' }}>
          {reportData && renderReportContent()}
          {toolbarPos.visible && (
            <Paper
              elevation={3}
              sx={{
                position: 'absolute',
                top: toolbarPos.top,
                left: toolbarPos.left,
                transform: 'translateX(-100%)',
                p: 0.5,
                borderRadius: 2,
                display: 'flex',
                gap: 0.5
              }}
            >
              <Tooltip title={askedTick ? 'Added to chat' : 'Ask in chat'}>
                <span>
                  <IconButton size="small" onClick={handleAskInChat} sx={{ width: 28, height: 28 }}>
                    <QuestionAnswerIcon fontSize="small" color={askedTick ? 'success' : 'action'} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={copiedTick ? 'Copied' : 'Copy'}>
                <span>
                  <IconButton size="small" onClick={handleCopySelection} sx={{ width: 28, height: 28 }}>
                    <ContentCopyIcon fontSize="small" color={copiedTick ? 'success' : 'action'} />
                  </IconButton>
                </span>
              </Tooltip>
            </Paper>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: '100%',
          maxWidth: '100vw',
          height: '100vh',
          zIndex: theme.zIndex.drawer + 1
        }
      }}
    >
      <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Report Viewer
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box ref={contentRef} sx={{ flex: 1, overflow: 'auto', position: 'relative' }}>
          {reportData && renderReportContent()}
          {toolbarPos.visible && (
            <Paper
              elevation={3}
              sx={{
                position: 'absolute',
                top: toolbarPos.top,
                left: toolbarPos.left,
                transform: 'translateX(-100%)',
                p: 0.5,
                borderRadius: 2,
                display: 'flex',
                gap: 0.5
              }}
            >
              <Tooltip title={askedTick ? 'Added to chat' : 'Ask in chat'}>
                <span>
                  <IconButton size="small" onClick={handleAskInChat} sx={{ width: 28, height: 28 }}>
                    <QuestionAnswerIcon fontSize="small" color={askedTick ? 'success' : 'action'} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={copiedTick ? 'Copied' : 'Copy'}>
                <span>
                  <IconButton size="small" onClick={handleCopySelection} sx={{ width: 28, height: 28 }}>
                    <ContentCopyIcon fontSize="small" color={copiedTick ? 'success' : 'action'} />
                  </IconButton>
                </span>
              </Tooltip>
            </Paper>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default ReportViewer;
